import React from "react";
import { makeStyles, useTheme, withStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import MuiButton from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import NoSsr from "@material-ui/core/NoSsr";
import useStores from "../../utils/useStores";
import LinkIcon from "@material-ui/icons/Link";
import Paper from "@material-ui/core/Paper";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Collapse from "@material-ui/core/Collapse";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import { observer } from "mobx-react";
import NextLink from "next/link";
import Tabs from "@material-ui/core/Tabs";
import MiuTab from "@material-ui/core/Tab";
import Link from "../Link";
import CloseIcon from "@material-ui/icons/Close";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import MoreGoogleAnalyticsText from "./more/GoogleAnalytics";
import MoreFacebookPixelText from "./more/FacebookPixel";
import MoreOutbrainText from "./more/Outbrain";
import MoreGoogleAdsText from "./more/GoogleAds";

const Button = withStyles((theme) => ({
  root: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
}))(MuiButton);

const Tab = withStyles((theme) => ({
  root: {},
}))(MiuTab);

const useCookieSectionStyles = makeStyles((theme) => ({
  moreButton: {
    marginLeft: theme.spacing(1),
    transition: theme.transitions.create("transform"),
    transform: (props) => (props.showMore ? "rotate(90deg)" : "rotate(0deg)"),
  },
  moreContent: {
    // margin: theme.spacing(4, 0),
  },
}));

const useStyles = makeStyles((theme) => ({
  root: {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2, 4),
    backgroundColor: theme.palette.common.white,
    borderTop: `1px solid ${theme.palette.orange.dark}`,
    zIndex: 9999,
  },
}));

export const useLinkContext = (context) => {
  if (process.env.NODE_ENV === "production") return "/";
  if (["depi", "depv"].indexOf(context) > -1) return `/${context}/`;
  return `/enplus/${context}/`;
};

const CookieSection = (props) => {
  const { onChange, checked, label, helperText, disabled, more } = props;
  const [showMore, setShowMore] = React.useState(false);
  const classes = useCookieSectionStyles({ showMore });

  const handleToggleShowMore = () => {
    setShowMore(!showMore);
  };

  return (
    <Box
      component={Paper}
      mb={2}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      p={2}
    >
      <FormControl>
        <FormControlLabel
          control={
            <Checkbox
              disabled={disabled}
              size="small"
              color="primary"
              checked={checked}
              onChange={onChange}
              name="necessary"
            />
          }
          label={
            <React.Fragment>
              {label}
              {more && (
                <IconButton
                  className={classes.moreButton}
                  onClick={handleToggleShowMore}
                  size="small"
                >
                  <ArrowRightIcon />
                </IconButton>
              )}
            </React.Fragment>
          }
        />
        <FormHelperText id="component-helper-text">{helperText}</FormHelperText>
      </FormControl>
      <Collapse in={showMore}>
        <div className={classes.moreContent}>{more}</div>
      </Collapse>
    </Box>
  );
};
CookieSection.defaultProps = {
  disabled: false,
  more: null,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const CookieBanner = () => {
  const { uiStore } = useStores();
  const [isVisible, setIsVisible] = React.useState(
    uiStore.functionalCookiesAccepted === null && !!uiStore.context
  );
  const [showSettings, setShowSettings] = React.useState(false);
  const [necessary, setNecessary] = React.useState(true);
  const [functional, setFunctional] = React.useState(
    uiStore.functionalCookiesAccepted || false
  );
  const [marketing, setMarketing] = React.useState(
    uiStore.marketingCookiesAccepted || false
  );
  const [googleAdsCookie, setGoogleAdsCookie] = React.useState(
    uiStore.adCookiesAccepted || uiStore.cookiesAccepted || false
  );
  const [googleAnalyticsCookie, setGoogleAnalyticsCookie] = React.useState(
    uiStore.gaCookiesAccepted || uiStore.cookiesAccepted || false
  );
  const [facebookPixelCookie, setFacebookPixelCookie] = React.useState(
    uiStore.fpCookiesAccepted || uiStore.cookiesAccepted || false
  );
  const [outbrainCookie, setOutbrainCookie] = React.useState(
    uiStore.obCookiesAccepted || uiStore.cookiesAccepted || false
  );
  const [cookieCategory, setCookieCategory] = React.useState(0);
  const classes = useStyles();

  const linkContext = useLinkContext(uiStore.context);

  const handleClickSettingsButton = () => {
    setShowSettings(!showSettings);
  };

  const handleChangeNecessary = () => {
    setNecessary(!necessary);
  };

  const handleChangeFunctional = () => {
    setFunctional(!functional);
  };

  const handleChangeMarketing = () => {
    setMarketing(!marketing);
  };

  const handleAcceptAll = () => {
    uiStore.acceptAllCookies();
    setIsVisible(false);
    uiStore.setShowCookiePreferences(false);
  };

  const handleDeclineAll = () => {
    uiStore.declineAllCookies();
    setIsVisible(false);
    uiStore.setShowCookiePreferences(false);
  };

  const handleAcceptFunctional = () => {
    uiStore.declineMarketingCookies();
    uiStore.declineGoogleAdsCookie();
    uiStore.acceptFunctionalCookies();
    uiStore.acceptFacebookPixelCookie();
    uiStore.acceptGoogleAnalyticsCookie();
    setIsVisible(false);
    uiStore.setShowCookiePreferences(false);
  };

  const handleAcceptMarketing = () => {
    uiStore.declineFunctionalCookies();
    uiStore.declineFacebookPixelCookie();
    uiStore.declineGoogleAnalyticsCookie();
    uiStore.acceptMarketingCookies();
    uiStore.acceptGoogleAdsCookie();
    uiStore.acceptOutbrainCookie();
    setIsVisible(false);
    uiStore.setShowCookiePreferences(false);
  };

  const handleSaveCustom = () => {
    if (cookieCategory === 0) {
      if (functional && marketing) {
        handleAcceptAll();
      } else if (functional) {
        handleAcceptFunctional();
      } else if (marketing) {
        handleAcceptMarketing();
      } else {
        handleDeclineAll();
      }
    } else {
      if (facebookPixelCookie) {
        uiStore.acceptFacebookPixelCookie();
      } else {
        uiStore.declineFacebookPixelCookie();
      }
      if (googleAnalyticsCookie) {
        uiStore.acceptGoogleAnalyticsCookie();
      } else {
        uiStore.declineGoogleAnalyticsCookie();
      }
      if (googleAnalyticsCookie && facebookPixelCookie) {
        uiStore.acceptFunctionalCookies();
      } else {
        uiStore.declineFunctionalCookies();
      }
      if (googleAdsCookie) {
        uiStore.acceptGoogleAdsCookie();
      } else {
        uiStore.declineGoogleAdsCookie();
      }
      if (outbrainCookie) {
        uiStore.acceptOutbrainCookie();
      } else {
        uiStore.declineOutbrainCookie();
      }
      if (outbrainCookie && googleAdsCookie) {
        uiStore.acceptMarketingCookies();
      } else {
        uiStore.declineMarketingCookies();
      }
      setIsVisible(false);
      uiStore.setShowCookiePreferences(false);
    }
  };

  const handleChangeCookieCategory = (event, newValue) => {
    setCookieCategory(newValue);
  };

  const handleToggleFacebookPixel = () => {
    setFacebookPixelCookie(!facebookPixelCookie);
  };

  const handleToggleOutbrainCookie = () => {
    setOutbrainCookie(!outbrainCookie);
  };

  const handleToggleGoogleAnalytics = () => {
    setGoogleAnalyticsCookie(!googleAnalyticsCookie);
  };

  const handleToggleGoogleAds = () => {
    setGoogleAdsCookie(!googleAdsCookie);
  };

  const showAvancedSettings = showSettings || uiStore.showCookiePreferences;

  React.useEffect(() => {
    setFunctional(uiStore.functionalCookiesAccepted);
    setMarketing(uiStore.marketingCookiesAccepted);
  }, [uiStore.functionalCookiesAccepted, uiStore.marketingCookiesAccepted]);

  React.useEffect(() => {
    setGoogleAnalyticsCookie(uiStore.gaCookiesAccepted);
  }, [uiStore.gaCookiesAccepted]);

  React.useEffect(() => {
    setFacebookPixelCookie(uiStore.fpCookiesAccepted);
  }, [uiStore.fpCookiesAccepted]);

  React.useEffect(() => {
    setGoogleAdsCookie(uiStore.adCookiesAccepted);
  }, [uiStore.adCookiesAccepted]);

  React.useEffect(() => {
    setOutbrainCookie(uiStore.obCookiesAccepted);
  }, [uiStore.obCookiesAccepted]);

  return (
    <NoSsr>
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        open={isVisible || uiStore.showCookiePreferences}
        aria-labelledby="cookie-consent-dialog"
      >
        <DialogTitle id="form-dialog-title">
          <Box display="flex" alignItems="center">
            Unsere Webseite verwendet Cookies
            {showSettings && (
              <Box ml="auto">
                <IconButton size="small" onClick={handleClickSettingsButton}>
                  <CloseIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Wir verwenden Cookies, um den Betrieb unserer Webseite
            sicherzustellen. Es werden auch Cookies für Marketing- und
            Analysezwecke genutzt, um die Funktion der Webseite durch Auswertung
            der erhobenen Daten ständig zu verbessern. Für die Erhebung dieser
            Daten benötigen wir Ihre Einwilligung, die Sie uns mit Klick auf
            „Alle annehmen und fortfahren“ erteilen. Um die Auswahl der
            erhobenen Daten anzupassen bzw. die Verwendung von Cookies zu
            verweigern, klicken Sie auf „Einstellungen anpassen“ bzw. „Ich bin
            nicht einverstanden“. Diese Einstellung können Sie jederzeit mit
            Klick auf den{" "}
            <Typography variant="body1" color="primary" component="span">
              <b>Fingerabdruck</b>
            </Typography>{" "}
            in der rechten, unteren Ecke anpassen. Weitere Informationen finden
            Sie in diesem Cookie-Banner und in der{" "}
            <Link color="primary" underline href={`${linkContext}datenschutz`}>
              <a>Datenschutzerklärung</a>
            </Link>
            .
          </DialogContentText>
          <Collapse in={showAvancedSettings}>
            <Box>
              <Typography gutterBottom variant="h6">
                Erweiterte Einstellungen
              </Typography>
              <Tabs
                variant="fullWidth"
                value={cookieCategory}
                onChange={handleChangeCookieCategory}
                aria-label="cookie category tabs"
                indicatorColor="primary"
              >
                <Tab label="Kategorien" {...a11yProps(0)} />
                <Tab label="Services" {...a11yProps(1)} />
              </Tabs>
              <Box bgcolor="grey.100" mx={-3} px={3} py={4}>
                {cookieCategory === 0 && (
                  <React.Fragment>
                    <CookieSection
                      label="Notwendig"
                      helperText={
                        <ul>
                          <li>
                            Unverzichtbar für die Grundfunktionen und den
                            Betrieb der Webseite
                          </li>
                          <li>
                            Speicherung von Formulardaten, Cookie-Einstellungen
                          </li>
                          <li>Gewährleistung der Log-In-Funktion</li>
                        </ul>
                      }
                      checked={necessary}
                      onChange={handleChangeNecessary}
                      disabled
                    />
                    <CookieSection
                      label="Funktionell"
                      helperText={
                        <ul>
                          <li>
                            Informationen über die Nutzung der Webseite (Google
                            Analytics)
                          </li>
                          <li>
                            Relevant zur Optimierung der Webseitenperformance
                            und -funktion
                          </li>
                        </ul>
                      }
                      checked={functional}
                      onChange={handleChangeFunctional}
                    />
                    <CookieSection
                      label="Marketing"
                      helperText={
                        <ul>
                          <li>
                            Erhöhung der Attraktivität von Werbeanzeigen online
                            und auf Social Media (z.B. durch GoogleAds)
                          </li>
                        </ul>
                      }
                      checked={marketing}
                      onChange={handleChangeMarketing}
                    />
                  </React.Fragment>
                )}
                {cookieCategory === 1 && (
                  <React.Fragment>
                    <CookieSection
                      label="Speicherung der Cookie-Einstellungen"
                      helperText="Notwendig"
                      checked={necessary}
                      onChange={handleChangeNecessary}
                      disabled
                    />
                    <CookieSection
                      label="Login für benutzerspezifische Inhalte"
                      helperText="Notwendig"
                      checked={necessary}
                      onChange={handleChangeNecessary}
                      disabled
                    />
                    <CookieSection
                      label="Google Analytics"
                      helperText="Funktionell"
                      checked={googleAnalyticsCookie}
                      onChange={handleToggleGoogleAnalytics}
                      more={<MoreGoogleAnalyticsText />}
                    />
                    {uiStore.context === "depi" && (
                      <CookieSection
                        label="Facebook Pixel"
                        helperText="Funktionell"
                        checked={facebookPixelCookie}
                        onChange={handleToggleFacebookPixel}
                        more={<MoreFacebookPixelText />}
                      />
                    )}
                    <CookieSection
                      label="GoogleAds"
                      helperText="Marketing"
                      checked={googleAdsCookie}
                      onChange={handleToggleGoogleAds}
                      more={<MoreGoogleAdsText />}
                    />
                    {uiStore.context === "depi" && (
                      <CookieSection
                        label="Outbrain"
                        helperText="Marketing"
                        checked={outbrainCookie}
                        onChange={handleToggleOutbrainCookie}
                        more={<MoreOutbrainText />}
                      />
                    )}
                  </React.Fragment>
                )}
              </Box>
            </Box>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Box display="flex" flexDirection="column" width="100%">
            <Box width="100%" display="flex" mb={1}>
              {showAvancedSettings && (
                <React.Fragment>
                  <Button
                    fullWidth
                    onClick={handleSaveCustom}
                    color="primary"
                    variant="outlined"
                  >
                    Auswahl Speichern
                  </Button>
                </React.Fragment>
              )}

              {!showAvancedSettings && (
                <Button
                  fullWidth
                  onClick={handleClickSettingsButton}
                  color="default"
                  variant="outlined"
                >
                  Einstellungen anpassen
                </Button>
              )}
              <Button
                fullWidth
                onClick={handleDeclineAll}
                color="secondary"
                variant="text"
              >
                Ich bin nicht einverstanden
              </Button>
              <Button fullWidth onClick={handleAcceptAll} color="primary">
                Alle annehmen und fortfahren
              </Button>
            </Box>
            <NextLink href={`${linkContext}impressum`} passHref>
              <Button
                target="_blank"
                component="a"
                startIcon={<LinkIcon />}
                size="small"
                variant="text"
              >
                Impressum
              </Button>
            </NextLink>
          </Box>
        </DialogActions>
      </Dialog>
    </NoSsr>
  );
};

export default observer(CookieBanner);
